import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import '../superadmin/DistrictsList.css';

const GettingStarted = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState({
    hasStudents: false,
    hasImportedReturningStudents: false,
    hasHouseholdInfo: false,
    hasStartedEnrollment: false,
    allStudentsSubmitted: false,
    hasSchoolLunch: false
  });
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  useEffect(() => {
    checkProgress();
    
    // Refresh progress when page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkProgress();
      }
    };
    
    // Listen for form submission events
    const handleFormSubmitted = () => {
      checkProgress();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('progressUpdated', handleFormSubmitted);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('progressUpdated', handleFormSubmitted);
    };
  }, [user?.role]); // Re-run when user loads (e.g. after auth) so parent-only checks work

  const checkProgress = async () => {
    try {
      setLoading(true);
      
      // Check for students
      const studentsResponse = await axios.get('/api/student-intake/my-intake');
      const hasStudents = studentsResponse.data && studentsResponse.data.length > 0;

      // Check for household information
      let hasHouseholdInfo = false;
      try {
        const householdResponse = await axios.get('/api/form-submissions/household-information');
        const householdData = householdResponse.data;
        hasHouseholdInfo = householdData && Object.keys(householdData).length > 0;
      } catch (error) {
        // No household info yet
        hasHouseholdInfo = false;
      }

      // Check for enrollment progress (form submissions with progress or submitted)
      let hasStartedEnrollment = false;
      let allStudentsSubmitted = false;
      try {
        const progressResponse = await axios.get('/api/form-submissions/progress?include_completed=true');
        const submissions = progressResponse.data || [];
        
        // Check if any enrollment forms have been started (have progress or are complete)
        // Exclude school lunch forms (form_instance_id is null and _form_type is school-lunch-application)
        const enrollmentSubmissions = submissions.filter(sub => {
          if (sub.form_instance_id === null) {
            return sub.submission_data?._form_type !== 'school-lunch-application';
          }
          return true;
        });
        
        hasStartedEnrollment = enrollmentSubmissions.length > 0;
        
        // Check if all students have completed submissions
        // Use form_status from student-intake API which already checks form_submission_intake_links
        if (hasStudents) {
          const students = studentsResponse.data;
          // Check if all students have form_status of 'completed'
          allStudentsSubmitted = students.length > 0 && 
            students.every(student => student.form_status === 'completed');
        }
      } catch (error) {
        console.error('Error checking enrollment progress:', error);
      }

      // Check for school lunch submission
      let hasSchoolLunch = false;
      try {
        const schoolLunchResponse = await axios.get('/api/form-submissions/progress?include_completed=true');
        const submissions = schoolLunchResponse.data || [];
        hasSchoolLunch = submissions.some(sub =>
          sub.form_instance_id === null &&
          sub.submission_data?._form_type === 'school-lunch-application' &&
          sub.is_complete
        );
      } catch (error) {
        console.error('Error checking school lunch:', error);
      }

      // Check for imported returning students (batch - 1 request instead of N)
      // Only run for parent users - district admins and super admins don't have this flow
      let hasImportedReturningStudents = false;
      if (hasStudents && user?.role === 'parent') {
        try {
          const students = studentsResponse.data;
          const ids = students.map(s => s.id).join(',');
          const batchResponse = await axios.get(`/api/returning-student-imports/check-batch?ids=${ids}`).catch(() => ({ data: {} }));
          const statusMap = batchResponse.data || {};
          hasImportedReturningStudents = Object.values(statusMap).some(s => s?.hasImportedData);
        } catch (error) {
          console.error('Error checking returning student imports:', error);
        }
      }

      setProgress({
        hasStudents,
        hasImportedReturningStudents,
        hasHouseholdInfo,
        hasStartedEnrollment,
        allStudentsSubmitted,
        hasSchoolLunch
      });
    } catch (error) {
      console.error('Error checking progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImportReturningStudents = async () => {
    try {
      setImporting(true);
      setImportResults(null);
      
      const response = await axios.post('/api/returning-student-imports/import');
      
      setImportResults(response.data);
      await checkProgress(); // Refresh progress to update checkbox
    } catch (error) {
      console.error('Import error:', error);
      console.error('Import error response:', error.response?.data);
      setImportResults({
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to import student data'
      });
    } finally {
      setImporting(false);
    }
  };

  const handleDismiss = () => {
    // Store preference in localStorage
    localStorage.setItem('gettingStartedDismissed', 'true');
    // Redirect to student intake page
    navigate('/parent/student-intake');
  };

  return (
    <>
      <style>{`
        .getting-started-checkbox {
          pointer-events: none !important;
          cursor: default !important;
        }
        .getting-started-checkbox:hover,
        .getting-started-checkbox:active,
        .getting-started-checkbox:focus {
          accent-color: #28a745 !important;
          cursor: default !important;
          outline: none !important;
          box-shadow: none !important;
        }
        .getting-started-checkbox:checked {
          accent-color: #28a745 !important;
        }
      `}</style>
      <div className="districts-list">
        <div className="page-header">
          <h2>Getting Started</h2>
          <button onClick={handleDismiss} className="btn-secondary">
            Dismiss
          </button>
        </div>

      <div style={{
        padding: '2rem',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginTop: '1.5rem'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h3 style={{ 
            color: '#2c3e50', 
            marginBottom: '1.5rem',
            fontSize: '1.5rem',
            borderBottom: '2px solid #667eea',
            paddingBottom: '0.5rem'
          }}>
            Welcome to {user?.district_name || 'District'} Parent Portal
          </h3>

          <div style={{ 
            marginBottom: '2rem',
            padding: '1.5rem',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            <h4 style={{ color: '#495057', marginBottom: '1.5rem', fontSize: '1.2rem' }}>
              📋 Getting Started Checklist
            </h4>
            
            {loading ? (
              <p style={{ color: '#6c757d' }}>Loading your progress...</p>
            ) : (
              <ol style={{ lineHeight: '2', color: '#495057', paddingLeft: '0', listStyle: 'none', counterReset: 'step-counter' }}>
                <li style={{ 
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: progress.hasStudents ? '#d4edda' : 'white',
                  borderRadius: '6px',
                  border: `2px solid ${progress.hasStudents ? '#28a745' : '#dee2e6'}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  counterIncrement: 'step-counter',
                  position: 'relative',
                  paddingLeft: '3rem'
                }}>
                  <input
                    type="checkbox"
                    checked={progress.hasStudents}
                    readOnly
                    disabled
                    className="getting-started-checkbox"
                    style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '1.25rem',
                      width: '20px',
                      height: '20px',
                      cursor: 'default',
                      accentColor: '#28a745',
                      pointerEvents: 'none'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '1.1rem', color: '#2c3e50' }}>
                      Add Students
                    </strong>
                    <p style={{ margin: '0.5rem 0 0 0', color: '#6c757d', lineHeight: '1.6' }}>
                      Fill out student name(s), SSN, and DOB for all your students that you will register.
                    </p>
                    {!progress.hasStudents && (
                      <button
                        onClick={() => navigate('/parent/student-intake/new')}
                        className="btn-primary"
                        style={{
                          marginTop: '0.75rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.9rem'
                        }}
                      >
                        Add Your First Student
                      </button>
                    )}
                  </div>
                </li>

                <li style={{ 
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: progress.hasImportedReturningStudents ? '#d4edda' : 'white',
                  borderRadius: '6px',
                  border: `2px solid ${progress.hasImportedReturningStudents ? '#28a745' : '#dee2e6'}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  counterIncrement: 'step-counter',
                  position: 'relative',
                  paddingLeft: '3rem'
                }}>
                  <input
                    type="checkbox"
                    checked={progress.hasImportedReturningStudents}
                    readOnly
                    disabled
                    className="getting-started-checkbox"
                    style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '1.25rem',
                      width: '20px',
                      height: '20px',
                      cursor: 'default',
                      accentColor: '#28a745',
                      pointerEvents: 'none'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '1.1rem', color: '#2c3e50' }}>
                      Import Current Students (Returning Students Only)
                    </strong>
                    <p style={{ margin: '0.5rem 0 0 0', color: '#6c757d', lineHeight: '1.6' }}>
                      If your students were previously enrolled in the district, click the button below to check for student records and import available data. 
                      This will automatically fill in information from your district's database where available. 
                      You must add your students first in "My Students" before the import can match them. If none of your students were previously enrolled, you should skip this section.
                    </p>
                    {progress.hasStudents && (
                      <>
                        <button
                          onClick={handleImportReturningStudents}
                          disabled={importing}
                          className="btn-primary"
                          style={{
                            marginTop: '0.75rem',
                            padding: '0.5rem 1rem',
                            fontSize: '0.9rem',
                            opacity: importing ? 0.6 : 1,
                            cursor: importing ? 'not-allowed' : 'pointer'
                          }}
                        >
                          {importing ? 'Importing...' : 'Import Current Students'}
                        </button>
                        {importResults && (
                          <div style={{
                            marginTop: '1rem',
                            padding: '0.75rem',
                            background: importResults.success ? '#d4edda' : '#f8d7da',
                            border: `1px solid ${importResults.success ? '#c3e6cb' : '#f5c6cb'}`,
                            borderRadius: '4px',
                            color: importResults.success ? '#155724' : '#721c24'
                          }}>
                            {importResults.success ? (
                              <div>
                                <strong>Import Results:</strong>
                                <ul style={{ margin: '0.5rem 0 0 1.5rem', padding: 0 }}>
                                  {importResults.results?.map((result, idx) => (
                                    <li key={idx} style={{ marginBottom: '0.25rem' }}>
                                      {result.studentName}: {result.status === 'imported' ? '✅ Imported successfully' : 'ℹ️ Already imported'}
                                    </li>
                                  ))}
                                </ul>
                                {importResults.imported > 0 && (
                                  <p style={{ margin: '0.5rem 0 0 0', fontWeight: 'bold' }}>
                                    {importResults.imported} student(s) imported successfully.
                                  </p>
                                )}
                                {importResults.alreadyImported > 0 && (
                                  <p style={{ margin: '0.5rem 0 0 0' }}>
                                    {importResults.alreadyImported} student(s) were already imported.
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div>
                                <strong>Import Failed:</strong> {importResults.error || 'Unknown error'}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                    {!progress.hasStudents && (
                      <p style={{ margin: '0.75rem 0 0 0', color: '#856404', fontStyle: 'italic' }}>
                        Please add your students first before importing data.
                      </p>
                    )}
                  </div>
                </li>

                <li style={{ 
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: progress.hasHouseholdInfo ? '#d4edda' : 'white',
                  borderRadius: '6px',
                  border: `2px solid ${progress.hasHouseholdInfo ? '#28a745' : '#dee2e6'}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  counterIncrement: 'step-counter',
                  position: 'relative',
                  paddingLeft: '3rem'
                }}>
                  <input
                    type="checkbox"
                    checked={progress.hasHouseholdInfo}
                    readOnly
                    disabled
                    className="getting-started-checkbox"
                    style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '1.25rem',
                      width: '20px',
                      height: '20px',
                      cursor: 'default',
                      accentColor: '#28a745',
                      pointerEvents: 'none'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '1.1rem', color: '#2c3e50' }}>
                      Fill out household information
                    </strong>
                    <p style={{ margin: '0.5rem 0 0 0', color: '#6c757d', lineHeight: '1.6' }}>
                      This is shared information for parent/guardian contact, home address/mailing address, and emergency contact information. 
                      This will allow you to fill all this out and your forms will pull the information in where applicable. 
                      Our goal is to reduce the amount of information you have to fill out multiple times.
                    </p>
                    {!progress.hasHouseholdInfo && (
                      <button
                        onClick={() => navigate('/parent/household-information')}
                        className="btn-primary"
                        style={{
                          marginTop: '0.75rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.9rem'
                        }}
                      >
                        Fill Out Household Information
                      </button>
                    )}
                  </div>
                </li>

                <li style={{ 
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: progress.hasStartedEnrollment ? '#d4edda' : 'white',
                  borderRadius: '6px',
                  border: `2px solid ${progress.hasStartedEnrollment ? '#28a745' : '#dee2e6'}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  counterIncrement: 'step-counter',
                  position: 'relative',
                  paddingLeft: '3rem'
                }}>
                  <input
                    type="checkbox"
                    checked={progress.hasStartedEnrollment}
                    readOnly
                    disabled
                    className="getting-started-checkbox"
                    style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '1.25rem',
                      width: '20px',
                      height: '20px',
                      cursor: 'default',
                      accentColor: '#28a745',
                      pointerEvents: 'none'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '1.1rem', color: '#2c3e50' }}>
                      Start Enrollment
                    </strong>
                    <p style={{ margin: '0.5rem 0 0 0', color: '#6c757d', lineHeight: '1.6' }}>
                      Click the "My Students" link and then click "Begin Enrollment" for your first student. 
                      Many fields will automatically fill from your household information. The first form in the list is a "Helper Form" 
                      that will allow you to fill information once per student and autofill that information across all forms. 
                      If you need to change information for just one form, you will find a checkmark marked "Autofill" in the top right 
                      corner of the form. Unchecking that will allow you to change the information without affecting your other forms. 
                      If you need to step away before submitting, just click the "Save progress" button in the bottom bar of the enrollment 
                      form page. You can come back to it later by clicking "Continue Enrollment". If you want to start fresh for any reason 
                      for a student, you can delete the form progress in the top bar that appears when you have form progress saved.
                    </p>
                    {!progress.hasStartedEnrollment && progress.hasStudents && (
                      <button
                        onClick={() => navigate('/parent/student-intake')}
                        className="btn-primary"
                        style={{
                          marginTop: '0.75rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.9rem'
                        }}
                      >
                        Start Enrollment
                      </button>
                    )}
                  </div>
                </li>

                <li style={{ 
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: progress.allStudentsSubmitted ? '#d4edda' : 'white',
                  borderRadius: '6px',
                  border: `2px solid ${progress.allStudentsSubmitted ? '#28a745' : '#dee2e6'}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  counterIncrement: 'step-counter',
                  position: 'relative',
                  paddingLeft: '3rem'
                }}>
                  <input
                    type="checkbox"
                    checked={progress.allStudentsSubmitted}
                    readOnly
                    disabled
                    className="getting-started-checkbox"
                    style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '1.25rem',
                      width: '20px',
                      height: '20px',
                      cursor: 'default',
                      accentColor: '#28a745',
                      pointerEvents: 'none'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '1.1rem', color: '#2c3e50' }}>
                      Submit forms for each student
                    </strong>
                    <p style={{ margin: '0.5rem 0 0 0', color: '#6c757d', lineHeight: '1.6' }}>
                      Continue submitting enrollment forms until you have completed enrollment for each student. 
                      From here, your district administrators will be able to enroll your students and contact you for any 
                      additional required information or documentation.
                    </p>
                    {!progress.allStudentsSubmitted && progress.hasStartedEnrollment && (
                      <button
                        onClick={() => navigate('/parent/student-intake')}
                        className="btn-primary"
                        style={{
                          marginTop: '0.75rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.9rem'
                        }}
                      >
                        Continue Enrollment
                      </button>
                    )}
                  </div>
                </li>

                <li style={{ 
                  marginBottom: '1.5rem',
                  padding: '1rem',
                  background: progress.hasSchoolLunch ? '#d4edda' : 'white',
                  borderRadius: '6px',
                  border: `2px solid ${progress.hasSchoolLunch ? '#28a745' : '#dee2e6'}`,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  counterIncrement: 'step-counter',
                  position: 'relative',
                  paddingLeft: '3rem'
                }}>
                  <input
                    type="checkbox"
                    checked={progress.hasSchoolLunch}
                    readOnly
                    disabled
                    className="getting-started-checkbox"
                    style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '1.25rem',
                      width: '20px',
                      height: '20px',
                      cursor: 'default',
                      accentColor: '#28a745',
                      pointerEvents: 'none'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: '1.1rem', color: '#2c3e50' }}>
                      School Lunch Reduced Cost Form
                    </strong>
                    <p style={{ margin: '0.5rem 0 0 0', color: '#6c757d', lineHeight: '1.6' }}>
                      If you wish to apply for free or reduced cost school lunch, click "View Forms" in your parent dashboard, 
                      then click "Start Application" for the School Lunch form. This will allow you to submit income information 
                      and student details for your entire household. This form is only filled out once per family, and should be 
                      resubmitted if your financial status changes. The school will be able to print and submit a pdf version of 
                      this submission to the state that matches official display requirements, without you having to fill out a paper form.
                    </p>
                    {!progress.hasSchoolLunch && (
                      <button
                        onClick={() => navigate('/parent/forms')}
                        className="btn-primary"
                        style={{
                          marginTop: '0.75rem',
                          padding: '0.5rem 1rem',
                          fontSize: '0.9rem'
                        }}
                      >
                        Apply for School Lunch
                      </button>
                    )}
                  </div>
                </li>
              </ol>
            )}
          </div>

          <div style={{ 
            marginBottom: '2rem',
            padding: '1.5rem',
            background: '#e7f3ff',
            borderRadius: '8px',
            border: '1px solid #b3d9ff'
          }}>
            <h4 style={{ color: '#004085', marginBottom: '1rem', fontSize: '1.2rem' }}>
              💡 Tips
            </h4>
            <ul style={{ lineHeight: '1.8', color: '#004085', paddingLeft: '1.5rem' }}>
              <li>You can save your progress on forms and come back to complete them later</li>
              <li>Some information you enter will automatically fill in other forms to save you time</li>
              <li>If you need help, contact your school district using the contact information shown in the header</li>
              <li>You can access this guide anytime from the "Getting Started" menu item</li>
            </ul>
          </div>

          <div style={{ 
            display: 'flex',
            gap: '1rem',
            marginTop: '2rem',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => navigate('/parent/student-intake/new')}
              className="btn-primary"
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem',
                fontWeight: 'bold'
              }}
            >
              Add Your First Student
            </button>
            <button
              onClick={handleDismiss}
              className="btn-secondary"
              style={{
                padding: '0.75rem 1.5rem',
                fontSize: '1rem'
              }}
            >
              I'm Ready to Get Started
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default GettingStarted;
